import { useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { DEEPGRAM_EARPHONE_CONFIG } from '@/constants/video-call';

interface DeepgramEarphoneTestResult {
  status: 'idle' | 'testing' | 'earphones_detected' | 'speakers_detected' | 'inconclusive' | 'error';
  confidence: number;
  isChecking: boolean;
  error?: string;
  testPhase?: 'setup' | 'connecting' | 'listening' | 'playing' | 'analyzing' | 'complete';
  capturedTranscript?: string;
  expectedPhrase?: string;
  matchScore?: number;
  deepgramConfidence?: number;
}

interface DeepgramResponse {
  type: string;
  channel?: {
    alternatives?: Array<{
      transcript: string;
      confidence?: number;
    }>;
  };
  is_final?: boolean;
}

// The browser hides why a WebSocket handshake failed (a rejected key and a blocked network look
// the same), so this names neither. Chrome's console logs the handshake status code.
const SERVICE_UNREACHABLE =
  "Couldn't connect to the audio verification service. Please test again; if it keeps failing, contact support.";

// An error whose message was written for the candidate. Any other error's text is internal and
// is never shown.
class CandidateFacingError extends Error {}

// Problems with the candidate's own microphone or browser, by getUserMedia's DOMException name.
// They are expected: the candidate is told what to do, and they are not reported to Sentry.
const MIC_BLOCKED = 'Microphone access is blocked. Allow the microphone for this site in your browser settings and test again.';
const NO_MIC = 'No microphone found. Connect your earphones, select them under Device Selection and test again.';
const DEVICE_ERROR_MESSAGES = new Map<string, string>([
  ['NotAllowedError', MIC_BLOCKED],
  ['SecurityError', MIC_BLOCKED],
  ['NotFoundError', NO_MIC],
  ['OverconstrainedError', NO_MIC],
  ['NotReadableError', 'Your microphone is in use by another app. Close apps such as Zoom or Teams and test again.'],
  ['NotSupportedError', "This browser can't run the earphone test. Please use the latest Chrome or Edge."],
]);

const isDeviceError = (error: unknown): boolean =>
  DEVICE_ERROR_MESSAGES.has((error as Error | undefined)?.name ?? '');

const testErrorMessage = (error: unknown): string => {
  if (error instanceof CandidateFacingError) return error.message;
  return DEVICE_ERROR_MESSAGES.get((error as Error | undefined)?.name ?? '')
    ?? 'The earphone test could not run. Please test again.';
};

export const useDeepgramEarphoneTest = () => {
  const [result, setResult] = useState<DeepgramEarphoneTestResult>({
    status: 'idle',
    confidence: 0,
    isChecking: false,
    testPhase: 'setup',
    expectedPhrase: DEEPGRAM_EARPHONE_CONFIG.TEST_PHRASE
  });

  const deepgramSocketRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const finalTranscriptRef = useRef<string>('');
  const deepgramConfidenceRef = useRef<number[]>([]);
  const testCompletedRef = useRef<boolean>(false);
  const micPeakRef = useRef<number | null>(null);
  const stopMicMeterRef = useRef<(() => void) | null>(null);

  const log = useCallback((message: string) => {
    // Only log important events, not verbose debugging
    if (/Starting|Result:|error|Transcribed|Playing|Failed to set|Heard/.test(message)) {
      console.log(`[EarphoneTest] ${message}`);
    }
  }, []);

  const updateResult = useCallback((update: Partial<DeepgramEarphoneTestResult>) => {
    setResult(prev => ({ ...prev, ...update }));
  }, []);

  const calculateSimilarity = useCallback((str1: string, str2: string): number => {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().replace(/[^\w\s]/g, '').trim();
    const s2 = str2.toLowerCase().replace(/[^\w\s]/g, '').trim();
    
    if (s1 === s2) return 1.0;
    
    // Word-based matching with fuzzy logic
    const words1 = s1.split(/\s+/).filter(w => w.length > 0);
    const words2 = s2.split(/\s+/).filter(w => w.length > 0);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    let matches = 0;
    for (const word1 of words1) {
      if (words2.some(word2 => 
        word2.includes(word1) || 
        word1.includes(word2) ||
        levenshteinDistance(word1, word2) <= 1
      )) {
        matches++;
      }
    }
    
    return matches / Math.max(words1.length, words2.length);
  }, []);

  const levenshteinDistance = useCallback((str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(0).map(() => Array(str1.length + 1).fill(0));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j - 1][i] + 1,     // deletion
          matrix[j][i - 1] + 1,     // insertion
          matrix[j - 1][i - 1] + cost // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }, []);

  const setupDeepgramConnection = useCallback(async (apiKey: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(DEEPGRAM_EARPHONE_CONFIG.DEEPGRAM_URL, ['token', apiKey]);
      deepgramSocketRef.current = socket;

      const timeoutId = setTimeout(() => {
        socket.close();
        reject(new CandidateFacingError(SERVICE_UNREACHABLE));
      }, DEEPGRAM_EARPHONE_CONFIG.WEBSOCKET_TIMEOUT);

      socket.onopen = () => {
        clearTimeout(timeoutId);
          
        // Send configuration
        const config = {
          type: 'Configure',
          processors: {
            stt: {
              model: DEEPGRAM_EARPHONE_CONFIG.DEEPGRAM_MODEL,
              language: DEEPGRAM_EARPHONE_CONFIG.DEEPGRAM_LANGUAGE,
              punctuate: true,
              interim_results: true,
              endpointing: 200,
              vad_turnoff: 300
            }
          }
        };
        socket.send(JSON.stringify(config));
        resolve();
      };

      socket.onmessage = (event) => {
        const data: DeepgramResponse = JSON.parse(event.data);
        handleDeepgramResponse(data);
      };

      socket.onerror = (error) => {
        clearTimeout(timeoutId);
        log(`Connection error: ${error}`);
        reject(new CandidateFacingError(SERVICE_UNREACHABLE));
      };

      socket.onclose = () => {
        clearTimeout(timeoutId);
      };
    });
  }, [log]);

  const handleDeepgramResponse = useCallback((data: DeepgramResponse) => {
    if (data.type === 'Results') {
      if (data.channel?.alternatives && data.channel.alternatives.length > 0) {
        const transcript = data.channel.alternatives[0].transcript;
        const confidence = data.channel.alternatives[0].confidence;
        const isFinal = data.is_final;
        
        if (transcript && !testCompletedRef.current) {
          // Only log final results to reduce console noise
          if (isFinal) {
            log(`Transcribed: "${transcript}" (confidence: ${confidence?.toFixed(3) || 'N/A'})`);
            finalTranscriptRef.current += transcript + ' ';
            if (confidence !== undefined) {
              deepgramConfidenceRef.current.push(confidence);
            }
            
            updateResult({ 
              capturedTranscript: finalTranscriptRef.current.trim() 
            });
          }
        }
      }
    }
  }, [log, updateResult]);

  // An empty transcript is what earphones should give, but a dead microphone gives it too: measure
  // the signal to tell them apart. micPeakRef stays null if the browser won't run the AudioContext.
  const startMicMeter = useCallback((stream: MediaStream) => {
    micPeakRef.current = null;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    const timer = setInterval(() => {
      if (context.state !== 'running') return;
      analyser.getFloatTimeDomainData(samples);
      const rms = Math.sqrt(samples.reduce((sum, s) => sum + s * s, 0) / samples.length);
      micPeakRef.current = Math.max(micPeakRef.current ?? 0, rms);
    }, 100);
    stopMicMeterRef.current = () => {
      clearInterval(timer);
      void context.close();
    };
    void context.resume();
  }, []);

  const setupMediaRecorder = useCallback((stream: MediaStream) => {
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && 
          deepgramSocketRef.current && 
          deepgramSocketRef.current.readyState === WebSocket.OPEN) {
        deepgramSocketRef.current.send(event.data);
      }
    };

    mediaRecorder.start(DEEPGRAM_EARPHONE_CONFIG.MEDIA_RECORDER_INTERVAL);
  }, [log]);

  const playTestAudio = useCallback((selectedAudioOutputDeviceId?: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      updateResult({ testPhase: 'playing' });

      // Try to use pre-recorded audio file first for device routing
      const tryAudioFile = async () => {
        try {
          const audio = new Audio('/audio/test-phrase.mp3');
          audio.volume = DEEPGRAM_EARPHONE_CONFIG.AUDIO_VOLUME;
          
          // Route to selected audio output device if provided
          if (selectedAudioOutputDeviceId && 'setSinkId' in HTMLMediaElement.prototype) {
            try {
              // @ts-ignore - setSinkId exists but TypeScript doesn't know about it
              await audio.setSinkId(selectedAudioOutputDeviceId);
              log(`Playing test phrase through selected device: ${selectedAudioOutputDeviceId.slice(0, 8)}...`);
            } catch (error) {
              log(`Failed to set audio device, using default: ${error}`);
            }
          }

          // Set up event handlers
          const handleEnd = () => {
            updateResult({ testPhase: 'analyzing' });
            setTimeout(() => {
              resolve();
            }, DEEPGRAM_EARPHONE_CONFIG.PROCESSING_DELAY);
          };

          const handleError = (error: any) => {
            log(`Audio file error: ${error}. Falling back to speech synthesis.`);
            // Fall back to speech synthesis
            useSpeechSynthesis();
          };

          audio.onended = handleEnd;
          audio.onerror = handleError;

          // Try to load and play the audio file
          await audio.play();
        } catch (error) {
          log(`Audio file not available: ${error}. Using speech synthesis.`);
          // Fall back to speech synthesis
          useSpeechSynthesis();
        }
      };

      // Fallback to original speech synthesis approach
      const useSpeechSynthesis = () => {
        log('Using speech synthesis for test phrase');
        
        const utterance = new SpeechSynthesisUtterance(DEEPGRAM_EARPHONE_CONFIG.TEST_PHRASE);
        utterance.rate = DEEPGRAM_EARPHONE_CONFIG.AUDIO_RATE;
        utterance.pitch = DEEPGRAM_EARPHONE_CONFIG.AUDIO_PITCH;
        utterance.volume = DEEPGRAM_EARPHONE_CONFIG.AUDIO_VOLUME;
        utterance.lang = DEEPGRAM_EARPHONE_CONFIG.DEEPGRAM_LANGUAGE;

        // Try to select a consistent voice across platforms
        const voices = speechSynthesis.getVoices();
        const preferredVoice = voices.find(voice => 
          voice.lang.startsWith('en') && 
          (voice.name.toLowerCase().includes('male') || voice.name.toLowerCase().includes('david'))
        ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
        
        if (preferredVoice) {
          utterance.voice = preferredVoice;
          log(`Using voice: ${preferredVoice.name} (${preferredVoice.lang})`);
        }

        // Note: Speech synthesis doesn't support device routing
        if (selectedAudioOutputDeviceId) {
          log('Note: Speech synthesis cannot be routed to selected device. Consider generating audio file.');
        }

        utterance.onend = () => {
          updateResult({ testPhase: 'analyzing' });
          setTimeout(() => {
            resolve();
          }, DEEPGRAM_EARPHONE_CONFIG.PROCESSING_DELAY);
        };

        utterance.onerror = (event) => {
          log(`Speech synthesis error: ${event.error}`);
          reject(new CandidateFacingError("Couldn't play the test sound. Check that your earphones are the selected audio output and test again."));
        };

        window.speechSynthesis.speak(utterance);
      };

      // Start with trying the audio file approach
      tryAudioFile();
    });
  }, [log, updateResult]);

  const cleanup = useCallback(() => {
    stopMicMeterRef.current?.();
    stopMicMeterRef.current = null;

    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // Close WebSocket
    if (deepgramSocketRef.current && deepgramSocketRef.current.readyState === WebSocket.OPEN) {
      deepgramSocketRef.current.close();
      deepgramSocketRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Stop speech synthesis
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    // Abort any pending operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [log]);

  const analyzeResults = useCallback((capturedText: string): DeepgramEarphoneTestResult => {
    const micPeak = micPeakRef.current;
    if (!capturedText && micPeak !== null && micPeak < DEEPGRAM_EARPHONE_CONFIG.MIN_MIC_RMS) {
      log(`Result: NO MIC SIGNAL (peak RMS ${micPeak})`);
      return {
        status: 'error',
        confidence: 0,
        isChecking: false,
        testPhase: 'complete',
        error: "We couldn't hear anything from your microphone. Check that it isn't muted and test again."
      };
    }

    const similarity = calculateSimilarity(DEEPGRAM_EARPHONE_CONFIG.TEST_PHRASE, capturedText);
    log(`Heard: "${capturedText}" (similarity ${similarity.toFixed(2)}, mic peak ${micPeak?.toFixed(4) ?? 'n/a'})`);
    const avgDeepgramConfidence = deepgramConfidenceRef.current.length > 0 
      ? deepgramConfidenceRef.current.reduce((a, b) => a + b, 0) / deepgramConfidenceRef.current.length 
      : 0;


    // Enhanced logic with Deepgram confidence validation
    let status: DeepgramEarphoneTestResult['status'];
    let confidence: number;

    if (similarity <= DEEPGRAM_EARPHONE_CONFIG.EARPHONES_THRESHOLD) {
      if (avgDeepgramConfidence < DEEPGRAM_EARPHONE_CONFIG.MIN_CONFIDENCE_THRESHOLD && similarity > 0.05) {
        // Low similarity + Low Deepgram confidence = Likely low volume speakers
        status = 'inconclusive';
        confidence = 30;
        log('Result: INCONCLUSIVE - Possible low volume speakers (poor transcription quality)');
      } else {
        // Low similarity + Good confidence = True earphones or user spoke something else
        status = 'earphones_detected';
        confidence = Math.min(95, 60 + ((1 - similarity) * 35));
        log('Result: EARPHONES DETECTED - No audio leakage');
      }
    } else if (similarity >= DEEPGRAM_EARPHONE_CONFIG.SPEAKERS_THRESHOLD) {
      // High similarity = Speakers detected
      status = 'speakers_detected';
      confidence = Math.min(95, 60 + (similarity * 35));
      log('Result: SPEAKERS DETECTED - Audio leakage detected');
    } else {
      // Medium similarity = Inconclusive
      status = 'inconclusive';
      confidence = 30 + (similarity * 20);
      log('Result: INCONCLUSIVE - Partial audio detected');
    }

    return {
      status,
      confidence: confidence / 100, // Convert to 0-1 range
      isChecking: false,
      testPhase: 'complete',
      capturedTranscript: capturedText,
      expectedPhrase: DEEPGRAM_EARPHONE_CONFIG.TEST_PHRASE,
      matchScore: similarity,
      deepgramConfidence: avgDeepgramConfidence
    };
  }, [calculateSimilarity, log]);

  const testEarphoneConnection = useCallback(async (selectedAudioDeviceId?: string, selectedAudioOutputDeviceId?: string): Promise<boolean> => {
    // Check for API key
    const apiKey = import.meta.env.VITE_DEEPGRAM_API_KEY;
    if (!apiKey || apiKey === 'your-deepgram-api-key') {
      const errorResult: DeepgramEarphoneTestResult = {
        status: 'error',
        confidence: 0,
        isChecking: false,
        testPhase: 'complete',
        error: 'The audio verification service is unavailable. Please contact support.'
      };
      console.error('[EarphoneTest] VITE_DEEPGRAM_API_KEY is not configured');
      setResult(errorResult);
      return false;
    }

    // Abort any existing test
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // Reset state
    finalTranscriptRef.current = '';
    deepgramConfidenceRef.current = [];
    testCompletedRef.current = false;

    updateResult({
      status: 'testing',
      confidence: 0,
      isChecking: true,
      testPhase: 'setup',
      error: undefined,
      capturedTranscript: '',
      matchScore: 0,
      deepgramConfidence: 0
    });

    try {
      log('🎯 Starting Deepgram-based earphone detection test...');

      // Get microphone access
      updateResult({ testPhase: 'setup' });
      
      const audioConstraints = {
        ...DEEPGRAM_EARPHONE_CONFIG.AUDIO_CONSTRAINTS,
        ...(selectedAudioDeviceId && { deviceId: { exact: selectedAudioDeviceId } })
      };
      
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      } catch (error) {
        // The selected microphone is gone (earphones switched since the device list was read):
        // test the default one instead of failing every retry the same way.
        const name = (error as Error | undefined)?.name;
        if (!selectedAudioDeviceId || (name !== 'OverconstrainedError' && name !== 'NotFoundError')) throw error;
        console.warn(`[EarphoneTest] Selected microphone unavailable (${name}), using the default microphone`);
        stream = await navigator.mediaDevices.getUserMedia({ audio: DEEPGRAM_EARPHONE_CONFIG.AUDIO_CONSTRAINTS });
      }

      if (signal.aborted) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }

      mediaStreamRef.current = stream;
      startMicMeter(stream);

      // Setup Deepgram connection
      updateResult({ testPhase: 'connecting' });
      await setupDeepgramConnection(apiKey);

      if (signal.aborted) {
        cleanup();
        return false;
      }

      // Setup audio recording
      setupMediaRecorder(stream);
      
      // Start listening phase
      updateResult({ testPhase: 'listening' });

      // Wait before playing audio
      await new Promise(resolve => setTimeout(resolve, DEEPGRAM_EARPHONE_CONFIG.STARTUP_DELAY));

      if (signal.aborted) {
        cleanup();
        return false;
      }

      // Play test audio
      await playTestAudio(selectedAudioOutputDeviceId);

      if (signal.aborted) {
        cleanup();
        return false;
      }

      // Analyze results
      const capturedText = finalTranscriptRef.current.trim();
      const analysisResult = analyzeResults(capturedText);
      
      // Cleanup resources
      cleanup();

      // Mark test as completed to prevent further updates
      testCompletedRef.current = true;
      
      // Set final result
      setResult(analysisResult);
      
      log(`Test completed: ${analysisResult.status} (${(analysisResult.confidence * 100).toFixed(0)}% confidence)`);
      
      return analysisResult.status === 'earphones_detected';

    } catch (error) {
      log(`Test error: ${error instanceof Error ? `${error.name}: ${error.message}` : 'Unknown error'}`);
      if (!isDeviceError(error)) {
        Sentry.captureException(error, { tags: { component: 'EarphoneTest' } });
      }

      cleanup();
      
      // Mark test as completed to prevent further updates
      testCompletedRef.current = true;
      
      const errorResult: DeepgramEarphoneTestResult = {
        status: 'error',
        confidence: 0,
        isChecking: false,
        testPhase: 'complete',
        error: testErrorMessage(error)
      };

      setResult(errorResult);
      return false;
    }
  }, [log, updateResult, setupDeepgramConnection, startMicMeter, setupMediaRecorder, playTestAudio, cleanup, analyzeResults]);

  const resetTest = useCallback(() => {
    log('Resetting test...');
    
    cleanup();
    
    // Reset refs
    finalTranscriptRef.current = '';
    deepgramConfidenceRef.current = [];
    testCompletedRef.current = false;

    setResult({
      status: 'idle',
      confidence: 0,
      isChecking: false,
      testPhase: 'setup',
      expectedPhrase: DEEPGRAM_EARPHONE_CONFIG.TEST_PHRASE
    });
  }, [log, cleanup]);

  const stopTest = useCallback(() => {
    log('Stopping test...');
    cleanup();
  }, [log, cleanup]);

  return { 
    result, 
    testEarphoneConnection, 
    resetTest, 
    stopTest,
    isSupported: true // Deepgram is always supported (depends on API key)
  };
};